import React, {useCallback, useEffect, useState} from "react";
import {NOTIFICATION_GROUP_TITLES, notificationErrorMessage} from "../api/cabinetNotifications";
import type {
  CabinetNotificationSettings as Settings, NotificationGroupInfo, NotificationGroupKey, TelegramTestResult,
} from "../api/cabinetNotifications";
import {useAuth} from "../auth";
import {copyToClipboard} from "./CopyButton";
import {formatMerchantDate} from "../utils";
import type {NotificationScope} from "./notificationScope";

function orderedGroups(settings: Settings, keys: NotificationScope["groupKeys"]): NotificationGroupInfo[] {
  return settings.groups.length ? settings.groups : keys.map(key => ({key, title: NOTIFICATION_GROUP_TITLES[key]}));
}
function sameSelection(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every(key => b.includes(key));
}
function telegramLink(url: string): string | undefined { return url.startsWith("https://t.me/") ? url : undefined }

export function NotificationSettings({scope}: {scope: NotificationScope}): JSX.Element {
  const {token} = useAuth();
  const {api} = scope;
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [cabinet, setCabinet] = useState<NotificationGroupKey[]>([]);
  const [telegram, setTelegram] = useState<NotificationGroupKey[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{kind: "ok" | "error"; text: string} | null>(null);
  const [busy, setBusy] = useState(false);
  const [tgError, setTgError] = useState<string | null>(null);
  const [tgNotice, setTgNotice] = useState<string | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);
  const [confirming, setConfirming] = useState<number | null>(null);
  const [testResults, setTestResults] = useState<TelegramTestResult | null>(null);

  const applyServer = useCallback((next: Settings, keepDraft: boolean): void => {
    setSettings(next);
    if (!keepDraft) { setCabinet(next.cabinet); setTelegram(next.telegramGroups); }
  }, []);
  const load = useCallback((): void => {
    setLoadError(null);
    api.getSettings(token).then(next => applyServer(next, false))
      .catch((err: unknown) => setLoadError(notificationErrorMessage(err)));
  }, [api, token, applyServer]);
  useEffect(() => { load(); }, [load]);

  const expiresAt = settings?.telegram.link?.expiresAt;
  useEffect(() => {
    if (!expiresAt) return;
    let active = true;
    const refresh = (): void => {
      if (Date.parse(expiresAt) <= Date.now()) {
        setSettings(prev => prev ? {...prev, telegram: {...prev.telegram, link: null}} : prev);
        return;
      }
      if (document.visibilityState !== "visible") return;
      api.getSettings(token).then(next => { if (active) setSettings(next); })
        .catch(() => undefined);
    };
    const timer = window.setInterval(refresh, 5000);
    document.addEventListener("visibilitychange", refresh);
    return () => { active = false; window.clearInterval(timer); document.removeEventListener("visibilitychange", refresh); };
  }, [api, expiresAt, token]);

  if (!settings) return (
    <section className="merchant-section-card merchant-notify-settings" aria-label="Что присылать">
      <h2>Что присылать</h2>
      {loadError ? <div role="alert"><p>{loadError}</p><button type="button" className="btn btn-ghost" onClick={load}>Повторить</button></div>
        : <p className="merchant-notify-muted">Загрузка…</p>}
    </section>
  );
  const dirty = !sameSelection(cabinet, settings.cabinet) || !sameSelection(telegram, settings.telegramGroups);
  const groups = orderedGroups(settings, scope.groupKeys);
  const connection = settings.telegram;
  const toggle = (channel: "cabinet" | "telegram", key: NotificationGroupKey): void => {
    const [current, setCurrent] = channel === "cabinet" ? [cabinet, setCabinet] : [telegram, setTelegram];
    setCurrent(current.includes(key) ? current.filter(item => item !== key) : [...current, key]);
    setSaveMessage(null);
  };
  const save = async (): Promise<void> => {
    setSaving(true); setSaveMessage(null);
    try {
      applyServer(await api.saveSettings(token, {cabinet, telegram}), false);
      setSaveMessage({kind: "ok", text: "Настройки сохранены."});
    } catch (err) { setSaveMessage({kind: "error", text: notificationErrorMessage(err)}); }
    finally { setSaving(false); }
  };
  const runTelegram = async (action: () => Promise<void>, notice = ""): Promise<void> => {
    setBusy(true); setTgError(null); setTgNotice(null); setCopyFailed(false);
    try { await action(); setTgNotice(notice); }
    catch (err) { setTgError(notificationErrorMessage(err)); }
    finally { setBusy(false); }
  };
  // Буфер обмена — не сервер: при отказе браузера показываем ссылку для ручного копирования.
  const copyLink = async (url: string): Promise<void> => {
    setTgError(null); setTgNotice(null); setCopyFailed(false);
    if (await copyToClipboard(url)) setTgNotice("Ссылка скопирована.");
    else setCopyFailed(true);
  };
  const createLink = (): Promise<void> => runTelegram(async () => {
    await api.createLink(token);
    applyServer(await api.getSettings(token), true);
  });
  const sendTest = (): Promise<void> => runTelegram(async () => {
    setTestResults(await api.testTelegram(token));
    applyServer(await api.getSettings(token), true);
  });

  return <>
      <section className="merchant-section-card merchant-notify-settings" aria-labelledby="merchant-notify-settings-title">
        <h2 id="merchant-notify-settings-title">Что присылать</h2>
        <p className="merchant-notify-muted">
          Отметьте, о чём сообщать в кабинете и в Telegram. Telegram заработает после подключения бота ниже.
        </p>
        <table className="merchant-notify-table" data-testid="notification-settings-table">
          <thead>
            <tr>
              <th scope="col">Событие</th>
              <th scope="col">Кабинет</th>
              <th scope="col">Telegram</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <tr key={group.key}>
                <th scope="row">{group.title}</th>
                <td>
                  <label className="merchant-notify-check">
                    <input
                      type="checkbox"
                      checked={cabinet.includes(group.key)}
                      onChange={() => toggle("cabinet", group.key)}
                      aria-label={`${group.title}: в кабинете`}
                      data-testid={`notify-cabinet-${group.key}`}
                    />
                  </label>
                </td>
                <td>
                  <label className="merchant-notify-check">
                    <input
                      type="checkbox"
                      disabled={!settings.telegram.available}
                      checked={telegram.includes(group.key)}
                      onChange={() => toggle("telegram", group.key)}
                      aria-label={`${group.title}: в Telegram`}
                      data-testid={`notify-telegram-${group.key}`}
                    />
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="merchant-notify-actions">
          <button
            className="btn btn-primary"
            type="button"
            onClick={() => void save()}
            disabled={!dirty || saving}
            data-testid="notification-settings-save"
          >
            {saving ? "Сохраняем…" : "Сохранить"}
          </button>
          {dirty && !saving && <span className="merchant-notify-muted">Есть несохранённые изменения</span>}
        </div>
        {saveMessage && (
          <div
            className={`alert ${saveMessage.kind === "ok" ? "alert-info" : "alert-error"}`}
            role={saveMessage.kind === "ok" ? "status" : "alert"}
            data-testid="notification-settings-message"
          >
            {saveMessage.text}
          </div>
        )}
      </section>

    <section className="merchant-section-card merchant-notify-telegram" aria-labelledby="merchant-notify-telegram-title"
      data-testid="telegram-block" data-state={!connection.available ? "unavailable" : connection.link ? "link" : "no-link"}>
      <h2 id="merchant-notify-telegram-title">Telegram</h2>
      {!connection.available ? <p data-testid="telegram-unavailable">Telegram сейчас недоступен</p> : <>
        {tgNotice && <div className="alert alert-info" role="status" data-testid="telegram-notice">{tgNotice}</div>}
        {tgError && <div className="alert alert-error" role="alert" data-testid="telegram-error">{tgError}</div>}
        <p className="merchant-notify-muted">Откройте ссылку сами или перешлите тем, кто должен получать уведомления.
          Каждый нажимает «Старт» в боте. Можно добавить бота в группу.</p>
        {connection.link ? <>
          <div className="merchant-notify-actions">
            <a className="btn btn-primary" href={telegramLink(connection.link.startUrl)} target="_blank" rel="noreferrer" data-testid="telegram-open">Открыть бота</a>
            <a className="btn btn-secondary" href={telegramLink(connection.link.groupUrl)} target="_blank" rel="noreferrer" data-testid="telegram-group">Добавить в группу</a>
            <button className="btn btn-ghost" type="button" disabled={busy} data-testid="telegram-copy"
              onClick={() => void copyLink(connection.link!.startUrl)}>Скопировать ссылку</button>
          </div>
          {copyFailed && <div className="alert alert-error" role="alert" data-testid="telegram-copy-failed">
            Браузер не дал скопировать. Выделите ссылку и скопируйте вручную:
            <input className="merchant-notify-manual-link" readOnly value={connection.link.startUrl}
              onFocus={event => event.currentTarget.select()} aria-label="Ссылка на бота" data-testid="telegram-copy-manual"/>
          </div>}
          <p className="merchant-notify-muted merchant-notify-gap" data-testid="telegram-expiry">Ссылка действует до {formatMerchantDate(connection.link.expiresAt)}</p>
          <div className="merchant-notify-actions">
            <button className="btn btn-ghost" type="button" disabled={busy} onClick={() => void createLink()} data-testid="telegram-new-link">Новая ссылка</button>
            <button className="btn btn-ghost" type="button" disabled={busy} data-testid="telegram-revoke" onClick={() => void runTelegram(async () => {
              applyServer(await api.revokeLink(token), true);
            }, "Ссылка отозвана. Подключённые чаты продолжают получать уведомления.")}>Отозвать ссылку</button>
          </div>
        </> : <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void createLink()} data-testid="telegram-connect">Подключить Telegram</button>}
        {connection.chats.length > 0 ? <>
          <ul className="merchant-notify-chats" data-testid="telegram-chats">
            {connection.chats.map(chat => <li key={chat.id} data-testid={`telegram-chat-${chat.id}`}>
              <strong>{chat.type === "private" ? chat.displayName || "Личный чат" : `Группа «${chat.displayName}»`}</strong>
              <span className="merchant-notify-muted">Подключён {formatMerchantDate(chat.connectedAt)}</span>
              <span data-testid={`telegram-chat-state-${chat.id}`} className={chat.state === "paused" ? "alert alert-error" : "merchant-notify-muted"}>
                {chat.state === "connected" ? "Работает" : "Пауза: бот не может писать в чат. Разблокируйте или верните бота в группу, затем отправьте тест или нажмите «Старт» по живой ссылке."}
              </span>
              {confirming === chat.id ? <div role="group" aria-label="Подтверждение отключения">
                <p>Отключить этот чат? Неотправленные сообщения для него будут отменены.</p>
                <div className="merchant-notify-actions">
                  <button type="button" className="btn btn-danger" disabled={busy} data-testid={`telegram-disconnect-confirm-${chat.id}`} onClick={() => void runTelegram(async () => {
                    applyServer(await api.disconnectChat(token, chat.id), true); setConfirming(null); setTestResults(null);
                  })}>Да, отключить</button>
                  <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setConfirming(null)}>Отмена</button>
                </div>
              </div> : <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => setConfirming(chat.id)} data-testid={`telegram-disconnect-${chat.id}`}>Отключить</button>}
            </li>)}
          </ul>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={() => void sendTest()} data-testid="telegram-test">Отправить тест</button>
        </> : <p className="merchant-notify-muted merchant-notify-gap" data-testid="telegram-no-chats">Подключённых чатов пока нет.</p>}
        {testResults && <ul className="merchant-notify-test-results" role="status" data-testid="telegram-test-results">
          {testResults.results.map(result => <li key={result.id}>{connection.chats.find(chat => chat.id === result.id)?.displayName || "Чат"}: {result.sent ? "Тест отправлен" : result.error || "Не отправлено"}</li>)}
        </ul>}
      </>}
    </section>
  </>;
}
